/**
 * Hands a CSV Blob to the browser download mechanism (Req 2.5).
 *
 * This mirrors the download handoff already used in this package — the
 * per-message HTML export in `ChatBoxComponent.exportAsHTML` and the dashboard
 * CSV export in `dashboard/plugins/weblabTracker/WeblabTrackerContent.tsx`:
 * create an object URL for the Blob, point a detached anchor carrying a
 * `download` attribute at it, click the anchor, then remove it and revoke the
 * URL. Nothing about that sequence is new here; what is new is that it is
 * reachable from a test and that a failure anywhere in it surfaces as one
 * error type (Req 7.2).
 *
 * No React, and every DOM touch goes through `deps.doc`, so the whole function
 * is drivable from a test.
 */

// ── Errors ─────────────────────────────────────────────────────────────────

/**
 * The browser refused the download: `createObjectURL` threw, the anchor's
 * `click()` threw, or the cleanup that follows threw. This is the trigger for
 * Req 7.2, and it is a distinct type from `TableStructureError` so the
 * download flow can tell "the table could not be converted" apart from "the
 * download did not complete".
 *
 * The original failure is kept as `cause` so a real browser failure is still
 * diagnosable after wrapping.
 */
export class DownloadFailedError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "DownloadFailedError";
    // Restores the prototype chain when this class is transpiled down to an
    // ES5 constructor function, so `instanceof DownloadFailedError` holds in
    // every build target.
    Object.setPrototypeOf(this, DownloadFailedError.prototype);
  }
}

/** Message carried by every `DownloadFailedError` this module throws. */
export const DOWNLOAD_FAILED_MESSAGE = "The CSV download did not complete.";

// ── Dependency seam ────────────────────────────────────────────────────────

/**
 * The three environment touch points of the handoff, injected rather than
 * reached for directly.
 *
 * This exists for testability, not for configurability: jsdom implements
 * neither `URL.createObjectURL` nor `URL.revokeObjectURL`, so a test that
 * wants to observe the emitted file — or to drive the failure path of Req 7.2
 * — passes a capturing stub here. Production code omits `deps` and gets the
 * real `URL` and `document`.
 */
export interface DownloadDeps {
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
  doc: Document;
}

/**
 * The real environment.
 *
 * Built fresh per call, and the two `URL` methods are wrapped rather than
 * referenced, so `URL.createObjectURL`, `URL.revokeObjectURL`, and `document`
 * are looked up when the download runs rather than when this module loads.
 * That keeps the module importable in an environment that installs those
 * globals after import — which is how jsdom-based tests behave.
 */
function realDownloadDeps(): DownloadDeps {
  return {
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
    doc: document,
  };
}

// ── Handoff ────────────────────────────────────────────────────────────────

/**
 * Hand `blob` to the browser as a download named `fileName` (Req 2.5).
 *
 * `fileName` reaches the anchor's `download` attribute as given. It arrives
 * already restricted to `[a-z0-9._-]` — sanitization lives in
 * `buildCsvFileName.sanitizeFileName` (Req 4.3), which is what keeps path
 * separators, quotes, and CR/LF out of the attribute. This function neither
 * repeats that work nor relies on it having happened.
 *
 * @throws {DownloadFailedError} if any step of the sequence throws (Req 7.2).
 */
export function downloadCsvFile(
  blob: Blob,
  fileName: string,
  deps: DownloadDeps = realDownloadDeps()
): void {
  try {
    let url: string | null = null;
    let anchor: HTMLAnchorElement | null = null;
    try {
      url = deps.createObjectURL(blob);
      anchor = deps.doc.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      deps.doc.body.appendChild(anchor);
      anchor.click();
    } finally {
      // Cleanup runs on the failure path too, so a throwing `click()` does not
      // leave an anchor in the document or leak a blob URL. `remove()` is a
      // no-op when the anchor was never appended.
      if (anchor !== null) {
        anchor.remove();
      }
      if (url !== null) {
        deps.revokeObjectURL(url);
      }
    }
  } catch (cause) {
    // Catches the body and the cleanup alike, so nothing escapes this module
    // unwrapped. When both throw, `cause` is the cleanup failure — the one
    // JavaScript keeps.
    throw new DownloadFailedError(DOWNLOAD_FAILED_MESSAGE, { cause });
  }
}
